import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { memoize } from 'micro-memoize';

import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { ExtendedLiveReportList, processDashboards } from '../../../processors/dashboards/dashboardsList';
import { currentUserQuery } from '../../../queries/currentUser';
import { liveReportsQuery } from '../../../queries/liveReports';
import { membersQuery } from '../../../queries/members';
import { performanceDashboardsQuery } from '../../../queries/performanceDashboards';

export interface DashboardsListViewState extends ApiViewState {
  currentUser?: CurrentUserCheck;
  dashboards?: ExtendedLiveReportList[];
}

export class DashboardsListView extends ApiView {
  private currentUserObs: ReturnType<typeof currentUserQuery>;
  private liveReportsObs: ReturnType<typeof liveReportsQuery>;
  private membersObs: ReturnType<typeof membersQuery>;
  private performanceDashboardsObs: ReturnType<typeof performanceDashboardsQuery>;

  private memoizedProcessDashboards: typeof processDashboards;

  public state: DashboardsListViewState = {
    ...getDefaultApiViewState(),
    currentUser: undefined,
    dashboards: []
  };

  constructor(
    private workspaceId: string,
    private organisationId: string,
    private isConsolidation: boolean = false,
    private flags: LDFlagSet = {}
  ) {
    super();

    // Observers
    this.currentUserObs = this.addQuery(currentUserQuery());
    this.liveReportsObs = this.addQuery(liveReportsQuery(workspaceId, organisationId, isConsolidation));
    this.membersObs = this.addQuery(membersQuery(workspaceId, organisationId, isConsolidation));
    this.performanceDashboardsObs = this.addQuery(performanceDashboardsQuery(workspaceId, organisationId));

    this.memoizedProcessDashboards = memoize(processDashboards);

    this.updateState();
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  private get currentUser() {
    return this.getResult(this.currentUserObs).data;
  }

  private get dashboards() {
    const rawReports = this.reports;
    const currentUser = this.currentUser;
    const members = this.members;
    const performanceDashboards = this.performanceDashboards;
    if (!currentUser || !rawReports || !members || !performanceDashboards) {
      return undefined;
    }
    return this.memoizedProcessDashboards(rawReports, currentUser, members, performanceDashboards, this.flags);
  }

  private get members() {
    return this.getResult(this.membersObs).data;
  }

  private get performanceDashboards() {
    return this.getResult(this.performanceDashboardsObs).data;
  }

  private get reports() {
    return this.getResult(this.liveReportsObs).data;
  }

  protected getNewState(): DashboardsListViewState {
    return {
      ...super.getNewState(),
      currentUser: this.currentUser,
      dashboards: this.dashboards
    };
  }
}
